import React, { useState, useCallback } from "react";

/* ── Types ── */
export interface FileNode {
  name: string;
  path: string;
  type: "file" | "dir";
  children?: FileNode[];
}

export interface FileTreeProps {
  files: FileNode[];
  onFileSelect: (path: string) => void;
  selectedPath?: string;
  defaultExpanded?: Set<string>;
}

/* ── Extension color mapping ── */
export const EXT_COLORS: Record<string, string> = {
  rs: "#dea584",
  ts: "#3178c6",
  tsx: "#3178c6",
  js: "#f7df1e",
  jsx: "#f7df1e",
  json: "#fbbf24",
  toml: "#34d399",
  yaml: "#cb171e",
  yml: "#cb171e",
  md: "#9090b8",
  css: "#563d7c",
  html: "#e34c26",
  py: "#3572a5",
  go: "#00add8",
  sh: "#89e051",
  lock: "#484f58",
};

export function getExt(name: string): string {
  return name.split(".").pop()?.toLowerCase() || "";
}

export function getExtColor(name: string): string {
  return EXT_COLORS[getExt(name)] || "#6b6b9a";
}

/* ── Indent guide lines ── */
function IndentGuides({ depth }: { depth: number }) {
  if (depth === 0) return null;
  const guides: React.ReactElement[] = [];
  for (let i = 0; i < depth; i++) {
    guides.push(
      <span
        key={i}
        className="ft-indent-guide"
        style={{ left: `${i * 12 + 14}px` }}
      />
    );
  }
  return <>{guides}</>;
}

/* ── Single tree node ── */
function TreeNode({
  node,
  depth,
  expanded,
  selectedPath,
  onToggle,
  onSelect,
}: {
  node: FileNode;
  depth: number;
  expanded: Set<string>;
  selectedPath?: string;
  onToggle: (path: string) => void;
  onSelect: (path: string) => void;
}) {
  const isDir = node.type === "dir";
  const isExpanded = expanded.has(node.path);
  const isActive = selectedPath === node.path;

  return (
    <div>
      <div
        className={`ft-tree-item${isActive ? " ft-tree-item--active" : ""}${isDir ? " ft-tree-item--dir" : ""}`}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        onClick={() => {
          if (isDir) onToggle(node.path);
          else onSelect(node.path);
        }}
      >
        <IndentGuides depth={depth} />
        <span
          className="ft-tree-item__chevron"
          style={isDir && isExpanded ? { transform: "rotate(90deg)" } : undefined}
        >
          {isDir ? "\u25B6" : ""}
        </span>
        {isDir ? (
          <svg className="ft-tree-item__folder-icon" viewBox="0 0 16 16" fill="none">
            <path
              d={
                isExpanded
                  ? "M1.5 13h13l-1.25-6H2.75L1.5 13zM1.5 3v4h4l1-2h7V3H1.5z"
                  : "M1.5 3v10h13V5H7.5l-1-2H1.5z"
              }
              fill={isExpanded ? "rgba(79,143,255,0.35)" : "rgba(139,148,158,0.35)"}
              stroke={isExpanded ? "rgba(79,143,255,0.6)" : "rgba(139,148,158,0.5)"}
              strokeWidth="0.8"
            />
          </svg>
        ) : (
          <span className="ft-tree-item__dot" style={{ background: getExtColor(node.name) }} />
        )}
        <span className="ft-tree-item__name">{node.name}</span>
      </div>
      {isDir && (
        <div
          className={`ft-tree-children${isExpanded ? " ft-tree-children--expanded" : " ft-tree-children--collapsed"}`}
          style={isExpanded ? { maxHeight: `${(node.children?.length || 0) * 200}px` } : undefined}
        >
          {node.children?.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              selectedPath={selectedPath}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── FileTree component ── */
export function FileTree({ files, onFileSelect, selectedPath, defaultExpanded }: FileTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(
    defaultExpanded ?? new Set<string>()
  );

  const toggleDir = useCallback((path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  if (files.length === 0) {
    return <div className="ft-empty">No files</div>;
  }

  return (
    <div className="ft-root">
      {files.map((node) => (
        <TreeNode
          key={node.path}
          node={node}
          depth={0}
          expanded={expanded}
          selectedPath={selectedPath}
          onToggle={toggleDir}
          onSelect={onFileSelect}
        />
      ))}
    </div>
  );
}
